import { api } from "@/lib/apiClient";

export interface TimesheetSummary {
  id: number;
  timesheet_number?: number | null;
  base_timesheet_id?: number | null;
  revision_index?: number | null;
  display_number?: string | null;
  auftrag_id?: number | null;
  period_start?: string | null;
  period_end?: string | null;
  status?: string | null;
  status_display?: string | null;
  created_at?: string | null;
  at_contact?: boolean | null;
  at_contact_at?: string | null;
  billing_assignee_login?: string | null;
  billing_assignee_name?: string | null;
  gd_rechnung_id?: number | null;
  invoice_document_id?: string | number | null;
  invoice_customer_document_id?: string | number | null;
  invoice_number?: string | null;
  invoice_value?: number | null;
  auftragsnummer?: string | null;
  ordnerpfad?: string | null;
  ordnernummer?: string | null;
  kunde?: string | null;
  ansprechpartner_nachname?: string | null;
  ansprechpartner_vorname?: string | null;
  ansprechpartner?: string | null;
  auftrag_display?: string | null;
  total_hours?: number | null;
  billing_question_count?: number | null;
  billing_question_open_count?: number | null;
  has_billing_questions?: boolean | null;
  billing_questions_open?: boolean | null;
  is_new_for_billing?: boolean | null;
  billing_last_answered_at?: string | null;
  billing_seen_at?: string | null;
  hero_project_id?: number | null;
  legacy_import?: boolean | null;
  has_newer_revision?: boolean;
  signed_outdated_revision?: boolean | null;
  jahr?: number | null;
  year?: number | null;
  ort?: string | null;
  ort_zusatz?: string | null;
  beschreibung?: string | null;
  hero_step_name?: string | null;
  project_completed?: boolean | null;
  hero_kunde?: string | null;
  use_hero_customer_name_in_projects?: boolean | null;
  hero_custom_fields?: unknown;
  sharepoint_path?: string | null;
  sharepoint_folder_id?: string | null;
  has_folder?: boolean | null;
  pfad?: string | null;
  project_name?: string | null;
  title?: string | null;
  ordner_beschreibung?: string | null;
  ordnerbeschreibung?: string | null;
  [key: string]: unknown;
}

export interface BillingMember {
  user_login?: string;
  login?: string;
  samAccountName?: string;
  displayName?: string;
}

/** Markiert neuere Revisionen; blendet ältere Zeilen in der Liste aus. */
export function annotateTimesheetRevisions(timesheets: TimesheetSummary[]): TimesheetSummary[] {
  const list = Array.isArray(timesheets) ? timesheets.map((ts) => ({ ...ts })) : [];
  const latestRevisionByBase = new Map<number, { revision: number; id: number }>();

  list.forEach((ts) => {
    const baseRaw = ts.base_timesheet_id ?? ts.id;
    const baseId = Number(baseRaw);
    const revision = Number(ts.revision_index ?? 0);
    const currentId = Number(ts.id ?? 0);
    if (!Number.isFinite(baseId)) return;

    const current = latestRevisionByBase.get(baseId);
    if (!current) {
      latestRevisionByBase.set(baseId, { revision, id: currentId });
      return;
    }
    if (revision > current.revision || (revision === current.revision && currentId > current.id)) {
      latestRevisionByBase.set(baseId, { revision, id: currentId });
    }
  });

  return list.map((ts) => {
    const baseRaw = ts.base_timesheet_id ?? ts.id;
    const baseId = Number(baseRaw);
    const revision = Number(ts.revision_index ?? 0);
    const currentId = Number(ts.id ?? 0);
    const latest = Number.isFinite(baseId) ? latestRevisionByBase.get(baseId) : null;
    const hasNewerRevision = Boolean(
      latest &&
        (latest.revision > revision || (latest.revision === revision && latest.id > currentId)),
    );
    return { ...ts, has_newer_revision: hasNewerRevision };
  });
}

/** Jahr-Vorfilter für die serverseitige Begrenzung der Summary-Liste. */
export type TimesheetSummaryYear = number | "S" | "all";

export interface ListTimesheetsSummaryOptions {
  /**
   * Hero-Custom-Fields je Zeile mitladen. Nur nötig, wenn eine Custom-Spalte
   * (cf_*) oder die Stundenverbrauch-Spalte sichtbar ist. Standard: false, damit
   * der reguläre Seitenaufruf und das Nav-Badge eine kleinere Payload bekommen.
   */
  includeHeroCustomFields?: boolean;
  /**
   * Serverseitige Vorfilterung nach Jahr: Jahreszahl (nur dieses Erstelljahr,
   * ohne S-Ordner), "S" (nur S-Ordner) oder "all" (alle – Standard, rückwärts-
   * kompatibel). Reduziert Payload/Query-Last bei großen Datenmengen deutlich.
   */
  jahr?: TimesheetSummaryYear;
}

function serializeSummaryYear(jahr: TimesheetSummaryYear | undefined): string {
  if (jahr === undefined || jahr === "all") return "all";
  if (jahr === "S") return "S";
  return String(jahr);
}

// Die (schwere) Zusammenfassung wird beim Öffnen der Stundennachweise-Seite
// häufig doppelt geladen: einmal von der Seite selbst und – für GF/Abrechnung –
// parallel vom Nav-Badge (offene Rückfragen). In-Flight-Dedup + ein sehr kurzes
// TTL kollabieren diese gleichzeitigen Aufrufe auf einen Request, ohne dass
// spätere (Realtime-)Reloads veraltete Daten bekommen. Pro Variante
// (Hero-Custom-Fields × Jahr) wird getrennt gecacht.
const TIMESHEET_SUMMARY_TTL_MS = 3_000;
type TimesheetSummaryCacheEntry = {
  cache: { at: number; value: TimesheetSummary[] } | null;
  inflight: Promise<TimesheetSummary[]> | null;
};
const _timesheetSummaryState: Record<string, TimesheetSummaryCacheEntry> = {};

function summaryCacheEntry(key: string): TimesheetSummaryCacheEntry {
  let entry = _timesheetSummaryState[key];
  if (!entry) {
    entry = { cache: null, inflight: null };
    _timesheetSummaryState[key] = entry;
  }
  return entry;
}

export async function listTimesheetsSummary(
  options?: ListTimesheetsSummaryOptions,
): Promise<TimesheetSummary[]> {
  const includeHero = options?.includeHeroCustomFields ?? false;
  const jahrParam = serializeSummaryYear(options?.jahr);
  const state = summaryCacheEntry(`${includeHero ? "full" : "lite"}:${jahrParam}`);

  if (state.cache && Date.now() - state.cache.at < TIMESHEET_SUMMARY_TTL_MS) {
    return state.cache.value;
  }
  if (state.inflight) return state.inflight;

  const query = `/timesheets/summary?include_archived=true&include_hero_custom_fields=${
    includeHero ? "true" : "false"
  }&jahr=${encodeURIComponent(jahrParam)}`;
  const inflight = api
    .get<TimesheetSummary[]>(query)
    .then((data) => {
      const list = Array.isArray(data) ? data : [];
      // Der Server liefert `has_newer_revision` jahresübergreifend korrekt.
      // Nur als Fallback (älterer Server ohne das Feld) client-seitig ableiten –
      // sonst würde die jahrweise geladene Teilmenge falsch annotiert.
      const needsAnnotate = list.some((ts) => typeof ts.has_newer_revision !== "boolean");
      const rows = needsAnnotate ? annotateTimesheetRevisions(list) : list;
      state.cache = { at: Date.now(), value: rows };
      return rows;
    })
    .finally(() => {
      if (state.inflight === inflight) state.inflight = null;
    });

  state.inflight = inflight;
  return inflight;
}

export interface TimesheetSummaryFacets {
  years: number[];
  hasS: boolean;
}

/** Leichte Filter-Facetten (verfügbare Jahre + ob S-Ordner existieren). */
export async function getTimesheetSummaryFacets(): Promise<TimesheetSummaryFacets> {
  const res = await api.get<{ years?: unknown; has_s?: unknown }>(
    "/timesheets/summary/facets",
  );
  const years = Array.isArray(res?.years)
    ? res.years
        .map((y) => Number(y))
        .filter((y) => Number.isFinite(y))
        .sort((a, b) => b - a)
    : [];
  return { years, hasS: Boolean(res?.has_s) };
}

export interface UnreadBillingAnswer {
  id: number;
  display_number?: string | null;
  auftrag_display?: string | null;
}

/**
 * Stundennachweise mit beantworteten, noch ungesehenen Rückfragen (jahres-
 * übergreifend, schlank) für das Abrechnungs-Banner.
 */
export async function listUnreadBillingAnswers(): Promise<UnreadBillingAnswer[]> {
  const data = await api.get<UnreadBillingAnswer[]>("/timesheets/billing/unread-answers");
  return Array.isArray(data) ? data : [];
}

export async function markTimesheetAtContact(
  timesheetId: number,
  atContact: boolean,
): Promise<void> {
  await api.post(`/timesheets/${timesheetId}/mark-at-contact`, { at_contact: atContact });
}

/** Lokales Patch-Feldset für optimistische NW-bei-AP-Updates in der UI. */
export function timesheetAtContactFields(
  atContact: boolean,
): Pick<TimesheetSummary, "at_contact" | "at_contact_at"> {
  return {
    at_contact: atContact,
    at_contact_at: atContact ? new Date().toISOString() : null,
  };
}

export async function listBillingMembers(): Promise<BillingMember[]> {
  const data = await api.get<BillingMember[]>(
    `/me/groups/members?group=${encodeURIComponent("Abrechnung")}`,
  );
  return Array.isArray(data) ? data : [];
}

/* ─────────────────────── Tabellen-Settings ─────────────────── */

export interface TimesheetColumnView {
  columns: string[];
  widths: Record<string, number>;
}

export interface TimesheetColumnSettings {
  desktop: TimesheetColumnView | null;
  mobile?: TimesheetColumnView | null;
}

interface TimesheetColumnSettingsResponse {
  table_key: string;
  settings: TimesheetColumnSettings | null;
}

const TIMESHEET_TABLE_KEY = "timesheets";

export async function getTimesheetColumnSettings(): Promise<TimesheetColumnSettings> {
  const res = await api.get<TimesheetColumnSettingsResponse>(
    `/me/table-settings/${TIMESHEET_TABLE_KEY}`,
  );
  const settings = res?.settings ?? null;
  return {
    desktop: settings?.desktop ?? null,
    mobile: settings?.mobile ?? null,
  };
}

export async function saveTimesheetColumnSettings(
  settings: TimesheetColumnSettings,
): Promise<TimesheetColumnSettings> {
  const res = await api.post<{ ok: true; settings: TimesheetColumnSettings }>(
    `/me/table-settings/${TIMESHEET_TABLE_KEY}`,
    { settings },
  );
  const saved = res?.settings ?? null;
  return {
    desktop: saved?.desktop ?? null,
    mobile: saved?.mobile ?? null,
  };
}
